import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { FeedbackFormData, FeedbackValidator } from 'lib/validators/feedback';
import { Textarea } from 'components/ui/textarea';
import { Rating } from 'components/Rating';
import { usePostFeedback } from 'services/profile/hooks';

export default function SendFeedbackPage() {
  const { t } = useTranslation();

  const form = useForm<FeedbackFormData>({
    resolver: zodResolver(FeedbackValidator),
    defaultValues: {
      feedback_title: '',
      feedback_content: '',
      rating: '',
    },
  });

  const { mutate, isPending } = usePostFeedback({
    onSuccess: () => {
      form.reset();
      toast.success(t('feedback_success'));
    },
  });

  function onSubmit(values: FeedbackFormData) {
    mutate({ ...values, rating: +values.rating });
  }

  const getTitleBasedOnRating = (rating: number) => {
    let title = '';
    if (rating === 1) title = t('very_bad');
    if (rating === 2) title = t('bad');
    if (rating === 3) title = t('average');
    if (rating === 4) title = t('good');
    if (rating === 5) title = t('excellent');
    return title;
  };

  return (
    <div className='flex items-center justify-center w-full h-full mt-20 mb-8 sm:my-8'>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className='w-full max-w-lg px-6 mx-auto space-y-8'
        >
          <div className='space-y-6'>
            <h2 className='text-2xl font-semibold text-center'>
              {t('feedback_form')}
            </h2>

            <FormField
              control={form.control}
              name='rating'
              render={({ field }) => (
                <FormItem className='flex flex-col items-center justify-center w-full'>
                  <FormLabel>{t('feedback_form_heading')}</FormLabel>

                  <FormControl className='mt-3'>
                    <Rating
                      {...field}
                      rating={+field.value}
                      onRatingChange={(rating) => {
                        form.setValue('rating', rating.toString());
                        form.setValue(
                          'feedback_title',
                          getTitleBasedOnRating(rating)
                        );
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name='feedback_title'
            render={({ field }) => (
              <FormItem className='w-full'>
                <FormLabel>{t('feedback_title')}</FormLabel>

                <FormControl>
                  <Input {...field} placeholder={t('feedback_title_example')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='feedback_content'
            render={({ field }) => (
              <FormItem className='w-full'>
                <FormLabel>{t('feedback_text_label')}</FormLabel>

                <FormControl>
                  <Textarea
                    {...field}
                    placeholder={t('feedback_text_example')}
                    className='border-t-0 border-b-2 border-l-0 border-r-0 rounded-none border-primary bg-background focus-visible:ring-0 focus-visible:ring-offset-0'
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className='flex justify-center !mt-10'>
            <Button
              type='submit'
              className='px-10 font-semibold rounded-full'
              disabled={!form.formState.isValid || isPending}
            >
              {isPending && (
                <Loader2
                  role='progressbar'
                  className='inline w-4 h-4 mr-2 animate-spin'
                />
              )}
              {t('submit')}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
